"use client";

import clsx from "clsx";
import { useState } from "react";
import ClientOnly from "./ClientOnly";

type FloatingParticlesProps = {
  /**
   * How many dots to scatter. The subpage layouts use the default; a dense hero
   * can ask for more.
   */
  count?: number;
  /** Extra classes on the layer itself — mainly for z-index. */
  className?: string;
};

/**
 * The ambient red dots that drift up the page behind subpage content.
 *
 * The layer clips its own overflow, so the parent only has to be positioned —
 * it does not need `overflow-hidden` of its own, which several layouts lack.
 *
 * Positions are drawn once per mount and held in state rather than recomputed
 * in render: a layout re-renders whenever its route changes, and re-rolling the
 * randoms there would make every dot jump to a new spot on each tab switch.
 * `ClientOnly` keeps the draw off the server, where it would not match.
 */
function ParticleLayer({ count, className }: Required<FloatingParticlesProps>) {
  const [dots] = useState(() =>
    Array.from({ length: count }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 5 + Math.random() * 3,
    }))
  );

  return (
    <div
      aria-hidden="true"
      className={clsx(
        "pointer-events-none absolute inset-0 overflow-hidden",
        className
      )}
    >
      {dots.map((dot, i) => (
        <span
          key={i}
          className="fp-dot"
          style={{
            left: `${dot.left}%`,
            top: `${dot.top}%`,
            animationDelay: `${dot.delay}s`,
            animationDuration: `${dot.duration}s`,
          }}
        />
      ))}

      <style jsx>{`
        .fp-dot {
          position: absolute;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: rgba(236, 25, 56, 0.5);
          opacity: 0;
          animation-name: fp-float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }

        /* Up and to the right, brightest at the ends and dimmest at the middle,
           where the dot is also at its largest — the swell reads as the dot
           passing closer to the viewer rather than simply moving. */
        @keyframes fp-float {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          50% {
            transform: translate(30px, -60px) scale(1.5);
            opacity: 0.6;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(60px, -120px) scale(1);
            opacity: 0;
          }
        }

        /* globals.css silences only a named list of effects, so this opts out
           on its own. */
        @media (prefers-reduced-motion: reduce) {
          .fp-dot {
            animation: none;
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default function FloatingParticles({
  count = 10,
  className = "",
}: FloatingParticlesProps) {
  return (
    <ClientOnly>
      <ParticleLayer count={count} className={className} />
    </ClientOnly>
  );
}
